import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { Bounce, toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import CustomButton from '../OnboardingComponents/CustomButton';
import CollaboratorSearchSelect, {
  collabSelectStyles,
  getCollaboratorUserId,
  isUuid,
} from '../DashboardComponents/CollaboratorSearchSelect';
import { get, post } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError } from '../../helpers/apiError';
import { fetchMyFlaggedMembers } from '../../pages/Flags/flagData';

const SEARCH_DEBOUNCE_MS = 300;

const resolveProjectCityId = (project) =>
  project?.cities?.[0]?.city_id ||
  project?.cities?.[0]?.city?.id ||
  project?.city_id ||
  null;

const mapProjectToOption = (project) => ({
  value: project.id,
  label: project.name || 'Untitled project',
  cityId: resolveProjectCityId(project),
  raw: project,
});

const getMemberUserId = (member) =>
  member?.user_id || member?.user?.id || '';

const projectIncludesUser = (project, userId) => {
  if (!userId) return false;
  const target = String(userId);
  const members = project?.members || project?.collaborators || [];
  return members.some((member) => {
    const memberId = getMemberUserId(member);
    return memberId && String(memberId) === target;
  });
};

const buildInviteSchema = (showProjectSelect) =>
  Yup.object({
    ...(showProjectSelect
      ? {
          project: Yup.object()
            .nullable()
            .required('Project is required'),
        }
      : {}),
    collaborators: Yup.array()
      .of(
        Yup.object({
          value: Yup.string().required(),
          label: Yup.string().required(),
          email: Yup.string()
            .trim()
            .transform((value, original) => {
              const next = value ?? original;
              return next === '' ? undefined : next;
            })
            .email('Enter a valid email')
            .when('value', {
              is: value => isUuid(value),
              then: schema => schema.notRequired(),
              otherwise: schema => schema.required('Email is required'),
            }),
        })
      )
      .min(1, 'Select at least one collaborator')
      .required('Select at least one collaborator'),
    message: Yup.string()
      .trim()
      .max(250, 'Message must be 250 characters or less'),
  });

/**
 * Shared invite-collaborator modal.
 * - Project context: pass projectId + cityId
 * - Invites page: pass showProjectSelect to pick a project first
 */
const AddCollaboratorModal = ({
  isOpen,
  onClose,
  projectId: projectIdProp = null,
  cityId: cityIdProp = null,
  showProjectSelect = false,
  onInvited,
  title = 'Add Collaborator',
  subtitle = 'Invite a new collaborator to join this project.',
  excludeMemberUserId = null,
  excludeProjectIds = [],
  presetCollaborators = [],
  lockCollaborators = false,
}) => {
  const [projectInputValue, setProjectInputValue] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectNextPageUrl, setProjectNextPageUrl] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingMoreProjects, setIsLoadingMoreProjects] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [excludeUserIds, setExcludeUserIds] = useState([]);
  const [excludeEmails, setExcludeEmails] = useState([]);
  const [flaggedUserIds, setFlaggedUserIds] = useState([]);
  const [flaggedEmails, setFlaggedEmails] = useState([]);

  const latestProjectRequest = useRef(0);
  const projectAbortRef = useRef(null);

  const debouncedSetProjectQuery = useMemo(
    () =>
      debounce((query) => setProjectSearchQuery(query), SEARCH_DEBOUNCE_MS),
    []
  );

  const inviteSchema = useMemo(
    () => buildInviteSchema(showProjectSelect),
    [showProjectSelect]
  );

  const formik = useFormik({
    initialValues: {
      project: null,
      collaborators: [],
      message: '',
    },
    validationSchema: inviteSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const selectedProjectId = showProjectSelect
        ? values.project?.value
        : projectIdProp;

      if (!selectedProjectId) {
        toast.error('Project is missing. Unable to send invite.', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        setSubmitting(false);
        return;
      }

      try {
        const collaboratorEmails = values.collaborators
          .filter(item => !item?.isFlagged && !item?.isDisabled)
          .map(item => String(item.email || '').trim())
          .filter(Boolean);
        const collaboratorUserIds = [
          ...new Set(
            values.collaborators
              .filter(item => !item?.isFlagged && !item?.isDisabled)
              .map(item => getCollaboratorUserId(item))
              .filter(Boolean)
          ),
        ];

        if (!collaboratorEmails.length && !collaboratorUserIds.length) {
          toast.error('Select at least one collaborator to invite.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
          setSubmitting(false);
          return;
        }

        const payload = {};
        if (collaboratorEmails.length) {
          payload.collaborator_emails = collaboratorEmails;
        }
        if (collaboratorUserIds.length) {
          payload.user_ids = collaboratorUserIds;
        }
        const note = values.message?.trim();
        if (note) {
          payload.description = note;
        }

        const response = await post(
          endpoints.inviteProjectMembers(selectedProjectId),
          payload,
          { skipErrorToast: true }
        );

        if (!response?.data?.success) {
          toastApiError(response, 'Failed to send invite');
          return;
        }

        toast.success(
          response?.data?.message || 'Invite sent successfully.',
          {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          }
        );
        resetForm();
        setProjectInputValue('');
        setProjectSearchQuery('');
        setProjectOptions([]);
        setProjectNextPageUrl(null);
        setProjectMenuOpen(false);
        onClose();
        await onInvited?.();
      } catch (err) {
        toastApiError(err, 'Failed to send invite');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const effectiveCityId = showProjectSelect
    ? formik.values.project?.cityId || null
    : cityIdProp;
  const effectiveProjectId = showProjectSelect
    ? formik.values.project?.value || null
    : projectIdProp;

  useEffect(() => {
    if (!isOpen) {
      setFlaggedUserIds([]);
      setFlaggedEmails([]);
      return undefined;
    }

    let cancelled = false;
    fetchMyFlaggedMembers()
      .then(result => {
        if (cancelled) return;
        setFlaggedUserIds(result?.userIds || []);
        setFlaggedEmails(result?.emails || []);
      })
      .catch(() => {
        if (cancelled) return;
        setFlaggedUserIds([]);
        setFlaggedEmails([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !effectiveProjectId) {
      setExcludeUserIds([]);
      setExcludeEmails([]);
      return undefined;
    }

    const controller = new AbortController();

    const collectFromInvitation = (row, ids, emails) => {
      const user = row?.user || row?.invite?.user || null;
      const userId =
        row?.user_id ||
        user?.id ||
        row?.invitee_id ||
        row?.member?.user_id ||
        '';
      const email =
        row?.email ||
        row?.invitee_email ||
        row?.invited_email ||
        user?.email ||
        row?.invite?.email ||
        '';
      if (userId) ids.add(String(userId).trim().toLowerCase());
      if (email) emails.add(String(email).trim().toLowerCase());
    };

    const loadExcludedCollaborators = async () => {
      const ids = new Set();
      const emails = new Set();

      try {
        const invitesResponse = await get(endpoints.getProjectInvitations, {
          params: {
            involvement: 'all',
            project_id: effectiveProjectId,
            page: 1,
            per_page: 100,
            page_size: 100,
            'with[]': ['user', 'invite'],
          },
          signal: controller.signal,
          skipErrorToast: true,
        });

        if (controller.signal.aborted) return;

        const inviteResult = invitesResponse?.data?.result || {};
        const inviteRows = Array.isArray(inviteResult)
          ? inviteResult
          : inviteResult?.data || [];
        inviteRows.forEach(row => collectFromInvitation(row, ids, emails));
      } catch {
        if (controller.signal.aborted) return;
      }

      if (controller.signal.aborted) return;
      setExcludeUserIds(Array.from(ids));
      setExcludeEmails(Array.from(emails));
    };

    loadExcludedCollaborators();
    return () => controller.abort();
  }, [isOpen, effectiveProjectId]);

  useEffect(() => {
    // Locked presets (e.g. public-profile invite) must stay selected.
    if (lockCollaborators) return;
    if (!excludeUserIds.length && !excludeEmails.length) return;
    const idSet = new Set(excludeUserIds);
    const emailSet = new Set(excludeEmails);
    const current = formik.values.collaborators || [];
    const filtered = current.filter(item => {
      const id = String(item?.value || '').trim().toLowerCase();
      const email = String(item?.email || '').trim().toLowerCase();
      const userId = String(getCollaboratorUserId(item) || '')
        .trim()
        .toLowerCase();
      if (id && idSet.has(id)) return false;
      if (userId && idSet.has(userId)) return false;
      if (email && emailSet.has(email)) return false;
      return true;
    });
    if (filtered.length !== current.length) {
      formik.setFieldValue('collaborators', filtered, false);
      formik.setFieldError('collaborators', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check when exclusions change
  }, [excludeUserIds, excludeEmails, lockCollaborators]);

  const excludedProjectIdSet = useMemo(
    () =>
      new Set(
        (excludeProjectIds || [])
          .map((id) => String(id || '').trim())
          .filter(Boolean)
      ),
    [excludeProjectIds]
  );

  const shouldExcludeProject = useCallback(
    (project) => {
      const projectId = String(project?.id || '');
      if (projectId && excludedProjectIdSet.has(projectId)) return true;
      return projectIncludesUser(project, excludeMemberUserId);
    },
    [excludeMemberUserId, excludedProjectIdSet]
  );

  const loadProjects = useCallback(
    async ({ query = '', pageUrl = null, isLoadMore = false } = {}) => {
      if (!showProjectSelect) return;

      const requestId = ++latestProjectRequest.current;
      if (projectAbortRef.current) {
        projectAbortRef.current.abort();
      }
      const controller = new AbortController();
      projectAbortRef.current = controller;

      if (isLoadMore) {
        setIsLoadingMoreProjects(true);
      } else {
        setIsLoadingProjects(true);
        setIsLoadingMoreProjects(false);
      }

      const withRelations = excludeMemberUserId
        ? ['cities.city', 'members.user']
        : ['cities.city'];

      const mapPage = (response) => {
        const result = response?.data?.result || {};
        const list = Array.isArray(result) ? result : result?.data || [];
        const mapped = list
          .filter((project) => !shouldExcludeProject(project))
          .map(mapProjectToOption);
        const next = Array.isArray(result)
          ? response?.data?.next_page_url
          : result?.next_page_url;
        return { mapped, next: next || null };
      };

      try {
        let mapped = [];
        let next = pageUrl;
        let pagesFetched = 0;
        const maxPages = 8;
        const minResults = isLoadMore ? 1 : 10;

        do {
          const response = await get(next || endpoints.getProjects, {
            signal: controller.signal,
            params: next
              ? undefined
              : {
                  search: query.trim() || undefined,
                  page: 1,
                  page_size: 10,
                  'with[]': withRelations,
                },
          });

          if (requestId !== latestProjectRequest.current) return;

          const page = mapPage(response);
          mapped = [...mapped, ...page.mapped];
          next = page.next;
          pagesFetched += 1;
        } while (
          next &&
          mapped.length < minResults &&
          pagesFetched < maxPages &&
          requestId === latestProjectRequest.current
        );

        if (requestId !== latestProjectRequest.current) return;

        setProjectOptions((prev) => {
          if (!isLoadMore) return mapped;
          const seen = new Set(prev.map((item) => String(item.value)));
          return [
            ...prev,
            ...mapped.filter((item) => !seen.has(String(item.value))),
          ];
        });
        setProjectNextPageUrl(next);
      } catch {
        if (requestId !== latestProjectRequest.current) return;
        if (!isLoadMore) {
          setProjectOptions([]);
          setProjectNextPageUrl(null);
        }
      } finally {
        if (requestId !== latestProjectRequest.current) return;
        if (isLoadMore) {
          setIsLoadingMoreProjects(false);
        } else {
          setIsLoadingProjects(false);
        }
      }
    },
    [excludeMemberUserId, shouldExcludeProject, showProjectSelect]
  );

  useEffect(() => {
    if (!isOpen || !showProjectSelect || !projectMenuOpen) return undefined;
    loadProjects({ query: projectSearchQuery });
    return undefined;
  }, [
    isOpen,
    showProjectSelect,
    projectMenuOpen,
    projectSearchQuery,
    loadProjects,
  ]);

  useEffect(() => {
    if (isOpen) return undefined;
    formik.resetForm();
    setProjectInputValue('');
    setProjectSearchQuery('');
    setProjectOptions([]);
    setProjectNextPageUrl(null);
    setProjectMenuOpen(false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !presetCollaborators.length) return undefined;
    formik.setFieldValue('collaborators', presetCollaborators, false);
    formik.setFieldError('collaborators', undefined);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, presetCollaborators]);

  // Keep locked invitee selected after project pick / exclusion refresh.
  useEffect(() => {
    if (!isOpen || !lockCollaborators || !presetCollaborators.length) {
      return undefined;
    }
    formik.setFieldValue('collaborators', presetCollaborators, false);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lockCollaborators, presetCollaborators, effectiveProjectId]);

  useEffect(
    () => () => {
      if (projectAbortRef.current) projectAbortRef.current.abort();
      debouncedSetProjectQuery.cancel();
    },
    [debouncedSetProjectQuery]
  );

  const resetCollaboratorSearch = () => {
    formik.setFieldValue('collaborators', []);
  };

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    setProjectInputValue('');
    setProjectSearchQuery('');
    setProjectOptions([]);
    setProjectNextPageUrl(null);
    setProjectMenuOpen(false);
    onClose();
  };

  const handleProjectInputChange = (newValue, meta) => {
    if (meta.action !== 'input-change') return;
    setProjectInputValue(newValue);
    debouncedSetProjectQuery(newValue);
  };

  const handleProjectScrollToBottom = () => {
    if (
      isLoadingProjects ||
      isLoadingMoreProjects ||
      !projectNextPageUrl
    ) {
      return;
    }
    loadProjects({
      query: projectSearchQuery,
      pageUrl: projectNextPageUrl,
      isLoadMore: true,
    });
  };

  const projectError =
    showProjectSelect &&
    formik.submitCount > 0 &&
    formik.errors.project
      ? typeof formik.errors.project === 'string'
        ? formik.errors.project
        : 'Project is required'
      : null;
  const collaboratorsError = (() => {
    if (!formik.submitCount || !formik.errors.collaborators) return null;
    if (typeof formik.errors.collaborators === 'string') {
      return formik.errors.collaborators;
    }
    if (Array.isArray(formik.errors.collaborators)) {
      const first = formik.errors.collaborators.find(Boolean);
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object') {
        return (
          first.email ||
          first.value ||
          first.label ||
          'One or more selected collaborators are invalid'
        );
      }
    }
    return null;
  })();
  const messageError =
    formik.submitCount > 0 && formik.errors.message
      ? formik.errors.message
      : null;

  const collaboratorUnavailableReason = showProjectSelect
    ? !effectiveProjectId
      ? 'Select a project first'
      : !effectiveCityId
        ? 'Selected project has no city'
        : null
    : !effectiveCityId
      ? 'Project city is unavailable'
      : null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      className="amok-modal amok-modal--shadow-session amok-modal--collab-invite"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body project-collab-modal__body">
        <form
          className="project-collab-modal project-collab-modal--invite"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <div className="project-collab-modal__main">
            <div className="project-collab-modal__header">
              <h3 className="project-collab-modal__title">{title}</h3>
              <p className="project-collab-modal__subtitle">{subtitle}</p>
            </div>

            <div className="project-collab-modal__fields">
              {showProjectSelect ? (
                <div className="project-collab-modal__field">
                  <label
                    className="project-collab-modal__label"
                    htmlFor="collab-invite-project"
                  >
                    Select Project
                  </label>
                  <div
                    className={`project-collab-modal__search${
                      projectError
                        ? ' project-collab-modal__search--error'
                        : ''
                    }`}
                  >
                    <Select
                      inputId="collab-invite-project"
                      className="project-collab-modal__user-select"
                      classNamePrefix="project-collab-modal-user"
                      options={projectOptions}
                      value={formik.values.project}
                      onChange={(selected) => {
                        formik.setFieldValue('project', selected || null);
                        formik.setFieldTouched('project', true, false);
                        if (!lockCollaborators) {
                          resetCollaboratorSearch();
                        }
                        setProjectInputValue('');
                      }}
                      onBlur={() => formik.setFieldTouched('project', true)}
                      inputValue={projectInputValue}
                      onInputChange={handleProjectInputChange}
                      isSearchable
                      isClearable
                      isLoading={isLoadingProjects}
                      menuIsOpen={projectMenuOpen}
                      onMenuOpen={() => setProjectMenuOpen(true)}
                      onMenuClose={() => setProjectMenuOpen(false)}
                      onMenuScrollToBottom={handleProjectScrollToBottom}
                      placeholder="Search projects..."
                      noOptionsMessage={() =>
                        projectSearchQuery
                          ? 'No projects found'
                          : excludeMemberUserId
                            ? 'No projects available to invite this member to'
                            : 'Type to search projects'
                      }
                      loadingMessage={() => 'Searching...'}
                      components={{
                        IndicatorSeparator: null,
                        DropdownIndicator: null,
                      }}
                      styles={collabSelectStyles(Boolean(projectError))}
                      menuPortalTarget={
                        typeof document !== 'undefined' ? document.body : null
                      }
                      menuPosition="fixed"
                      aria-label="Select Project"
                    />
                  </div>
                  {projectError ? (
                    <span className="project-collab-modal__error">
                      {projectError}
                    </span>
                  ) : null}
                </div>
              ) : null}

              <CollaboratorSearchSelect
                cityId={effectiveCityId}
                value={formik.values.collaborators}
                onChange={selected => {
                  if (lockCollaborators) return;
                  formik.setFieldValue('collaborators', selected, false);
                  formik.setFieldError('collaborators', undefined);
                }}
                onBlur={() => formik.setFieldTouched('collaborators', true, false)}
                disabled={lockCollaborators || Boolean(collaboratorUnavailableReason)}
                disabledReason={
                  lockCollaborators ? null : collaboratorUnavailableReason
                }
                skipCityCheck={lockCollaborators}
                error={collaboratorsError}
                enabled={isOpen}
                inputId="collab-invite-search"
                allowCreate={!lockCollaborators}
                excludeUserIds={excludeUserIds}
                excludeEmails={excludeEmails}
                flaggedUserIds={flaggedUserIds}
                flaggedEmails={flaggedEmails}
              />

              <div className="project-collab-modal__field">
                <div className="project-collab-modal__label-row">
                  <label
                    className="project-collab-modal__label"
                    htmlFor="collab-invite-message"
                  >
                    Message
                  </label>
                  <span className="project-collab-modal__optional">
                    (optional)
                  </span>
                </div>
                <textarea
                  id="collab-invite-message"
                  name="message"
                  className="project-collab-modal__note"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Add a personal note for the collaborator..."
                  maxLength={250}
                  aria-invalid={messageError ? 'true' : undefined}
                />
                <div className="project-collab-modal__note-meta">
                  {messageError ? (
                    <span className="project-collab-modal__error">
                      {messageError}
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className="project-collab-modal__char-count">
                    {formik.values.message.length}/250
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="project-collab-modal__actions">
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              onClick={handleClose}
              disabled={formik.isSubmitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting}
            >
              Send Invite
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default AddCollaboratorModal;
